// The documents grid's Versions column (#13682): a row's place in its
// document lineage family, in the words and order of the Versions section in
// Details. Both read the family through `lineageStatus.ts`, so the grid and
// Details cannot disagree about what a document is.
import {
  isCopyOf,
  noteOf,
  statusOf,
  versionBlocks,
} from "@/components/FileViewer/lineageStatus";
import type { DocumentLineage } from "@/components/FileViewer/services/useDocumentMetadata";
import type { EntityRow } from "../Dashboard/types";

/** The column's name, as the grid's views and group_by name it. */
export const VERSIONS_COLUMN = "Versions";
/** The built-in fact id the document schema gives the column. */
export const VERSIONS_FACT_ID = "versions";

/** What a row's Versions cell says. */
export interface VersionPlace {
  status: string;
  /** The likelihood note after a suggested member's status, as Details
   * shows it; null for a fact. */
  note: string | null;
  isCopy: boolean;
  /** The family's newest content state's own row: the one row reading
   * Latest, set in medium weight. */
  isLatest: boolean;
}

/** The row's place in its family, or null when the family does not hold
 * it (the viewer cannot open it). */
export function versionPlace(
  rowId: string,
  lineage: DocumentLineage | null
): VersionPlace | null {
  if (lineage === null) return null;
  for (const block of versionBlocks(lineage)) {
    for (const member of [block.head, ...block.copies]) {
      if (member.blob_id !== rowId) continue;
      const isCopy = isCopyOf(member, block);
      return {
        status: statusOf(member, block),
        note: noteOf(member),
        isCopy,
        isLatest: !isCopy && block.back === 0,
      };
    }
  }
  return null;
}

/** What a family's band is named: its latest document's filename as the
 * viewer sees it (the backend marks the viewer's effective latest). */
export function familyName(lineage: DocumentLineage): string {
  const latest = lineage.members.find((m) => m.is_latest);
  return (latest ?? lineage.members[0])?.filename ?? "";
}

/** The band for the documents with no other version in the result. */
export const NO_OTHER_VERSIONS = "No other versions";

/** Where a banded row sits in its family's run of rows: the rail starts at
 * the first row's node and ends at the last row's. Null for a row that is
 * not in a family's band (the page is not grouped on Versions, or the row
 * has no other version in the result), and for a band of one row, which
 * draws no rail: a first grouping field can split a family, so a Versions
 * band at the second rank may hold one row. */
export function versionsRail(
  rows: readonly EntityRow[],
  index: number
): { isFirst: boolean; isLast: boolean } | null {
  const row = rows[index];
  if (!row?.versions?.banded) return null;
  const family = row.versions.family;
  const sameBand = (other: EntityRow | undefined): boolean =>
    other?.versions?.banded === true &&
    other.versions.family === family &&
    other.group_rank === row.group_rank;
  const isFirst = !sameBand(rows[index - 1]);
  const isLast = !sameBand(rows[index + 1]);
  return isFirst && isLast ? null : { isFirst, isLast };
}

/** Rows in a family's Details order: the viewer's effective latest's content
 * state first (its head, then its copies), then the older states newest
 * first, as `versionBlocks` lays them out. */
function familyOrder(lineage: DocumentLineage): Map<string, number> {
  const order = new Map<string, number>();
  for (const block of versionBlocks(lineage)) {
    for (const member of [block.head, ...block.copies]) {
      order.set(member.blob_id, order.size);
    }
  }
  return order;
}

/** A page's rows with each Versions band the page holds whole put in its
 * family's Details order (#13682). The query orders a family by its stored
 * latest, which decides what a capped band keeps; the viewer's effective
 * latest can differ (the stored latest, or the pin, is one they cannot
 * open), and then the one row reading Latest would not lead and the rail
 * would run backwards. A band the page holds only part of keeps the query's
 * order, since its missing rows would land in the gaps. */
export function inFamilyOrder(rows: EntityRow[]): EntityRow[] {
  const out: EntityRow[] = [];
  let start = 0;
  while (start < rows.length) {
    const first = rows[start];
    let end = start + 1;
    const family = first.versions?.banded ? first.versions.family : null;
    if (family !== null) {
      while (
        end < rows.length &&
        rows[end].versions?.banded === true &&
        rows[end].versions?.family === family &&
        rows[end].group_rank === first.group_rank
      ) {
        end++;
      }
    }
    const run = rows.slice(start, end);
    const whole = first.group_size == null || run.length >= first.group_size;
    const lineage = first.versions?.lineage ?? null;
    if (family !== null && whole && lineage !== null) {
      const order = familyOrder(lineage);
      const at = (row: EntityRow): number =>
        order.get(row.id) ?? Number.MAX_SAFE_INTEGER;
      run.sort((a, b) => at(a) - at(b));
    }
    out.push(...run);
    start = end;
  }
  return out;
}
